import { NgOptimizedImage } from "@angular/common";
import { Component, computed, inject, input, signal } from "@angular/core";
import { SvgIconComponent } from "angular-svg-icon";
import { GetDirectusFilePipe } from "../../../../../../common/pipes/get-directus-file.pipe";
import { GetDirectusImagePipe } from "../../../../../../common/pipes/get-directus-image.pipe";
import { MimeTypeCheckPipe } from "../../../../../../common/pipes/mime-type-check.pipe";
import { MIME_TYPES } from "../../../../../api/common/model-enums/mime-types.enum";
import { Files } from "../../../../../api/packbo/models/files";

export type MediaType = "image" | "video";

@Component({
    selector: "packbo-supplier-files",
    templateUrl: "./supplier-files.template.html",
    imports: [GetDirectusImagePipe, GetDirectusFilePipe, MimeTypeCheckPipe, SvgIconComponent, NgOptimizedImage],
    providers: [GetDirectusImagePipe, GetDirectusFilePipe]
})
export class SupplierFilesComponent {
    private readonly getDirectusImagePipe = inject(GetDirectusImagePipe);
    private readonly getDirectusFilePipe = inject(GetDirectusFilePipe);

    public readonly file = input.required<Files>();
    public readonly allImages = input.required<Files[]>();
    public readonly currentImageIndex = input.required<number>();
    public readonly MIME_TYPES = MIME_TYPES;

    public readonly isLightboxOpen = signal(false);
    public readonly lightboxMediaSrc = signal("");
    public readonly lightboxMediaAlt = signal("");
    public readonly lightboxMediaType = signal<MediaType>("image");
    public readonly currentIndex = signal(0);
    public readonly navigationState = computed(() => ({
        hasPrevious: this.currentIndex() > 0,
        hasNext: this.currentIndex() < this.allImages().length - 1
    }));

    public openLightbox(): void {
        const mediaType = this.getMediaType(this.file());
        const mediaSrc = this.getMediaSrc(this.file(), mediaType);
        const mediaAlt = this.file().title || "Media";

        this.lightboxMediaSrc.set(mediaSrc);
        this.lightboxMediaAlt.set(mediaAlt);
        this.lightboxMediaType.set(mediaType);
        this.currentIndex.set(this.currentImageIndex());
        this.isLightboxOpen.set(true);
    }

    public closeLightbox(): void {
        this.isLightboxOpen.set(false);
    }

    public goToPrevious(): void {
        const currentIndex = this.currentIndex();
        if (currentIndex > 0) {
            const previousMedia = this.allImages()[currentIndex - 1];
            const mediaType = this.getMediaType(previousMedia);
            const mediaSrc = this.getMediaSrc(previousMedia, mediaType);
            const mediaAlt = previousMedia.title || "Media";

            this.lightboxMediaSrc.set(mediaSrc);
            this.lightboxMediaAlt.set(mediaAlt);
            this.lightboxMediaType.set(mediaType);
            this.currentIndex.set(currentIndex - 1);
        }
    }

    public goToNext(): void {
        const currentIndex = this.currentIndex();
        if (currentIndex < this.allImages().length - 1) {
            const nextMedia = this.allImages()[currentIndex + 1];
            const mediaType = this.getMediaType(nextMedia);
            const mediaSrc = this.getMediaSrc(nextMedia, mediaType);
            const mediaAlt = nextMedia.title || "Media";

            this.lightboxMediaSrc.set(mediaSrc);
            this.lightboxMediaAlt.set(mediaAlt);
            this.lightboxMediaType.set(mediaType);
            this.currentIndex.set(currentIndex + 1);
        }
    }

    private getMediaType(file: Files): MediaType {
        if (this.MIME_TYPES.PICTURE.includes(file.type ?? "")) {
            return "image";
        } else if (this.MIME_TYPES.VIDEO.includes(file.type ?? "")) {
            return "video";
        }
        return "image";
    }

    private getMediaSrc(file: Files, mediaType?: MediaType): string {
        const type = mediaType ?? this.getMediaType(file);
        return type === "image" ? this.getDirectusImagePipe.transform(file) : this.getDirectusFilePipe.transform(file);
    }
}
